import {
  Box,
  Button,
  Container,
  FormField,
  Header,
  Icon,
  Input,
  Modal,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-core';
import React, { useState } from 'react';
import { EditCollectionsEpicsTabProps } from './EditCollection';
import constants from '../../common/constants';
import CollectionEpicWrapper from './CollectionEpicWrapper';
import { SortableUserStories } from './SortableCollectionStories';
import { MAX_TITLE_LENGTH, MAX_DESCRIPTION_LENGTH, reorderCollectionEpics } from './utils';
import { EDIT_COLLECTION_CHAR_LIMIT } from '../../api/UtilsApi';
import { SortableList, SortableRenderArgs, useSortableItem } from '../v2-common/SortableList';
import { SortableItemHandle } from '../v2-common/SortableItemHandle';

// Stable across renders so SortableList's memoized id list stays valid.
const keyOfEpic = (epic: CollectionEpicWrapper) => epic.id;

function CollectionEpicHeader({ epics, setEpics, epicIdx }: EditCollectionsEpicsTabProps) {
  const [deleteEpicModalVisible, setDeleteEpicModalVisible] = useState<boolean>(false);

  const updateEpicTitle = (newTitle: string, epicNumber: number) => {
    const epicCopy = [...epics];
    // @ts-expect-error TS2532 - strict migration
    epicCopy[epicNumber].title = newTitle;
    setEpics(epicCopy);
  };

  const updateEpicDescription = (newDescr: string, epicNumber: number) => {
    const epicCopy = [...epics];
    // @ts-expect-error TS2532 - strict migration
    epicCopy[epicNumber].description = newDescr;
    setEpics(epicCopy);
  };

  const handleDeleteEpic = (epicNumber: number) => {
    const epicCopy = [...epics];
    epicCopy.splice(epicNumber, 1);
    setEpics(epicCopy);
    setDeleteEpicModalVisible(false);
  };

  const renderEpicHeader = () => {
    const epicNumber = epicIdx ?? 0;
    const epicInputTitle = 'Epic ' + (epicNumber + 1) + ' title*';
    // @ts-expect-error TS2532 - strict migration
    const charsRemainingDescr = MAX_DESCRIPTION_LENGTH - epics[epicNumber].description.length;
    const epicDescriptionLimitText =
      charsRemainingDescr + constants.EDIT_COLLECTION_CHAR_LIMIT_TEXT;
    // @ts-expect-error TS2532 - strict migration
    const charsRemainingTitle = MAX_TITLE_LENGTH - epics[epicNumber].title.length;
    const epicTitleLimitText = charsRemainingTitle + constants.EDIT_COLLECTION_CHAR_LIMIT_TEXT;

    return (
      <Header
        variant="h3"
        actions={
          <div>
            <SpaceBetween direction="horizontal" size="xs">
              <Modal
                visible={deleteEpicModalVisible}
                onDismiss={() => setDeleteEpicModalVisible(false)}
                header={constants.DELETE_COLLECTION_EPIC_HEADER_TEXT + (epicNumber + 1)}
                closeAriaLabel="Close modal"
                footer={
                  <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button variant="link" onClick={() => setDeleteEpicModalVisible(false)}>
                        No
                      </Button>
                      <Button variant="primary" onClick={() => handleDeleteEpic(epicNumber)}>
                        Yes
                      </Button>
                    </SpaceBetween>
                  </Box>
                }
              >
                {constants.DELETE_COLLECTION_EPIC_MODAL_TEXT}
              </Modal>
              <button
                disabled={epics.length <= 1}
                onClick={() => setDeleteEpicModalVisible(true)}
                className="edit-collection-button"
              >
                <Icon size="medium" name="remove" />
              </button>
              <DragHandle />
            </SpaceBetween>
          </div>
        }
      >
        <FormField
          label={epicInputTitle}
          errorText={
            // @ts-expect-error TS2532 - strict migration
            epics[epicNumber]?.title.length > EDIT_COLLECTION_CHAR_LIMIT &&
            constants.COLLECTION_INPUT_CHARACTER_LIMIT_ERROR
          }
          constraintText={
            // @ts-expect-error TS2532 - strict migration
            epics[epicNumber]?.title.length > EDIT_COLLECTION_CHAR_LIMIT ? (
              <></>
            ) : (
              <Box color="text-body-secondary">{epicTitleLimitText}</Box>
            )
          }
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          <Input
            placeholder={constants.EDIT_COLLECTION_EPIC_TITLE_PLACEHOLDER}
            value={epics[epicNumber]?.title ?? ''}
            onChange={(event) => updateEpicTitle(event.detail.value, epicNumber)}
            data-edit-collection-epic-header
            ariaRequired
          />
        </FormField>
        <p />
        <FormField
          label={constants.EDIT_COLLECTION_DESCRIPTION_HEADER}
          errorText={
            // @ts-expect-error TS2532 - strict migration
            epics[epicNumber]?.description.length > EDIT_COLLECTION_CHAR_LIMIT &&
            constants.COLLECTION_INPUT_CHARACTER_LIMIT_ERROR
          }
          constraintText={
            // @ts-expect-error TS2532 - strict migration
            epics[epicNumber]?.description.length > EDIT_COLLECTION_CHAR_LIMIT ? (
              <></>
            ) : (
              <Box color="text-body-secondary">{epicDescriptionLimitText}</Box>
            )
          }
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          <Textarea
            placeholder={constants.EDIT_COLLECTION_EPIC_DESCRIPTION_PLACEHOLDER}
            onChange={(event) => updateEpicDescription(event.detail.value, epicNumber)}
            value={epics[epicNumber]?.description ?? ''}
            ariaRequired
          />
        </FormField>
      </Header>
    );
  };

  return renderEpicHeader();
}

interface EpicItemProps {
  readonly epic: CollectionEpicWrapper;
  readonly epicNumber: number;
  readonly epics: CollectionEpicWrapper[];
  readonly setEpics: (epics: CollectionEpicWrapper[]) => void;
  readonly setNodeRef: SortableRenderArgs['setNodeRef'];
  readonly style: SortableRenderArgs['style'];
}

// One epic card; its header drag handle binds to this card's SortableList item.
function EpicItem({ epic, epicNumber, epics, setEpics, setNodeRef, style }: EpicItemProps) {
  return (
    <div ref={setNodeRef} style={style}>
      <Box margin={{ bottom: 'l' }}>
        <Container
          header={<CollectionEpicHeader epics={epics} setEpics={setEpics} epicIdx={epicNumber} />}
        >
          <SortableUserStories
            epics={epics}
            setEpics={setEpics}
            epic={epic}
            epicNumber={epicNumber}
          />
        </Container>
      </Box>
    </div>
  );
}

/**
 * Drag handle for the enclosing collection epic or story, styled like the editor's icon buttons
 * and greyed out when there is nothing to reorder.
 */
export function DragHandle() {
  const { isDisabled } = useSortableItem();
  return (
    <SortableItemHandle ariaLabel="Reorder">
      <span className="edit-collection-button">
        <Icon name="drag-indicator" size="medium" variant={isDisabled ? 'disabled' : 'normal'} />
      </span>
    </SortableItemHandle>
  );
}

interface SortableEpicsProps {
  readonly epics: CollectionEpicWrapper[];
  readonly setEpics: (epics: CollectionEpicWrapper[]) => void;
}

/** Reorderable list of collection epics, each with its own reorderable story table. */
export function SortableEpics({ epics, setEpics }: SortableEpicsProps) {
  const handleReorder = (newOrderIds: readonly string[]) => {
    setEpics(reorderCollectionEpics(epics, newOrderIds));
  };

  const renderEpic = (epic: CollectionEpicWrapper, { setNodeRef, style }: SortableRenderArgs) => (
    <EpicItem
      epic={epic}
      epicNumber={epics.indexOf(epic)}
      epics={epics}
      setEpics={setEpics}
      setNodeRef={setNodeRef}
      style={style}
    />
  );

  return (
    <SortableList
      items={epics}
      keyExtractor={keyOfEpic}
      onReorder={handleReorder}
      isDisabled={epics.length <= 1}
      item={renderEpic}
    />
  );
}
